//@core
const { getStore } = require('core/storeHelper');

// @vendors
const Immutable = require('immutable');
const trim = require('lodash/string/trim');
const flattenDeep = require('lodash/array/flattenDeep');
const get = require('lodash/object/get');

// @utilities
const { hash } = require('utilities/hash');

// @constants
const actionTypes = require('constants/actionTypes');
const { EURO_TEXT } = require('constants/index');

const initialState = Immutable.fromJS({});
const initialStateForValuesRequest = Immutable.fromJS({
    byId: {},
    byOrder: [],
    isFetching: true,
    success: false,
    error: '',
    cryptoFilteredIds: [],
});

function prepareMovements(movements = []) {
    let byId = {};
    let byOrder = [];

    movements.forEach(movement => {
        const movementNotNull = !!movement.get('valueCode') && !!movement.get('emisionCode');

        if (movementNotNull) {
            const id = hash([movement.get('valueCode')] + movement.get('emisionCode'));
            const titles = movement.get('titles');

            byId[id] = {
                id,
                valueCode: movement.get('valueCode'),
                emisionCode: movement.get('emisionCode'),
                description: movement.get('name') || '',
                type: trim(movement.get('productType') || ''),
                titles: isNaN(titles) ? '' : titles,
                date: movement.get('quoteDate') || null,
                hour: movement.get('quoteTime') || null,
                price: {
                    amount: movement.get('quote') || 0,
                    currency: movement.get('quoteCurrency') || EURO_TEXT
                },
                value: {
                    amount: movement.get('totalValue') || 0,
                    currency: movement.get('totalValueCurrency') || EURO_TEXT
                }
            };

            byOrder.push(id);
        }
    });

    return {
        byId,
        byOrder
    };
}

function prepareCryptoMovements(movements = [], contract, catalogueResponse) {
    let byId = {};
    let byOrder = [];
    let filteredMovements = [];
    const immMyInvestments = getStore().getState().brokerMyInvestments;
    const immStocks = immMyInvestments.getIn(['products', 'crypto', 'stocks']) || Immutable.List();
    const immProductsAsociatedToAccountContract = immStocks.size > 0 && immStocks.filter(stock => stock.get('contract') === contract);
    movements.forEach(movement => {
        const walletInfo = immProductsAsociatedToAccountContract.find(stock => stock.get('isin') === movement.getIn(['targetInstrument', 'isin']).replace(/-/g, ''));
        const id = hash(movement.getIn(['targetInstrument', 'isin']).replace(/-/g, ''));
        const titles = walletInfo && walletInfo.get('shares');
        const catalogIntrumentData = catalogueResponse
            ?.cryptocurrencies
            ?.instrumentsList
            ?.find(instrument =>
                instrument.isin === movement.getIn(['targetInstrument', 'isin']).replace(/-/g, '')
            ) || {};
        filteredMovements.push(movement.get('id'));
        if (!byId[id]) {
            byId[id] = {
                id,
                notHashedId: movement.get('id'),
                description: movement.get('targetInstrument').get('name') || '',
                titles: isNaN(titles) ? '' : titles,
                date: catalogIntrumentData?.quotationDate || null,
                hour: catalogIntrumentData?.quotationTime || null,
                price: {
                    amount: catalogIntrumentData?.quotation|| 0,
                    currency: catalogIntrumentData?.currency || EURO_TEXT,
                },
                value: {
                    amount: walletInfo ? walletInfo.get('totalValue') : 0,
                    currency: walletInfo ? walletInfo.get('totalValueCurrency') : EURO_TEXT,
                },
                movements: [movement.toJS()],
            };
            byOrder.push(id);
        } else {
            byId[id].movements.push(movement.toJS());
        }
    });
    return {
        byId,
        byOrder,
        filteredMovements
    };
}

function getValuesSuccess(immState, contractId, immStocks) {
    const preparedMovements = prepareMovements(immStocks);

    return immState.mergeDeep({
        [contractId]: {
            error: '',
            isFetching: false,
            success: true,
            byId: preparedMovements.byId,
            byOrder: preparedMovements.byOrder
        }
    });
}

function getCryptoSuccess(immState, portfolioId, immCrypto, contract, catalogueResponse) {
    const preparedMovements = prepareCryptoMovements(immCrypto, contract, catalogueResponse);

    return immState.mergeDeep({
        [portfolioId]: {
            error: '',
            isFetching: false,
            success: true,
            byId: preparedMovements.byId,
            byOrder: preparedMovements.byOrder,
            cryptoFilteredIds: preparedMovements.filteredMovements,
        }
    });
}

function contractAllValuesToPrepareMovementsAdapter(contractAllMovements) {
    const movements = get(contractAllMovements, 'listaMovimientos.movimientoContrato');
    const movementsAsArray = flattenDeep([movements]);
    const movementsAdaptedToPrepareMovements = movementsAsArray.map((movement) => {
        return {
            valueCode: get(movement, 'codValor'),
            emisionCode: get(movement, 'codEmision'),
            name: get(movement, 'descValor'),
            productType: '',
            titles: get(movement, 'titulos'),
            quoteDate: get(movement, 'fechaCotizacion'),
            quoteTime: get(movement, 'horaCotizacion'),
            quote: get(movement, 'cotizacion.numeroimportevalores'),
            quoteCurrency: get(movement, 'cotizacion.divisa'),
            totalValue: get(movement, 'valoracion.importe'),
            totalValueCurrency: get(movement, 'valoracion.divisa')
        };
    });

    return Immutable.fromJS(movementsAdaptedToPrepareMovements);
}

function brokerContractValues(state = initialState, action) {
    switch (action.type) {
        case actionTypes.BROKER_CONTRACT_VALUES_REQUEST:
            return state.mergeDeep({
                [action.payload.contractId]: initialStateForValuesRequest
            });
        case actionTypes.BROKER_CRYPTO_CONTRACT_VALUES_REQUEST:
            return state.mergeDeep({
                [action.payload.portfolioId]: initialStateForValuesRequest
            });
        case actionTypes.BROKER_CONTRACT_ALL_VALUES_SUCCESS:
            return getValuesSuccess(
                state,
                action.payload.contractId,
                contractAllValuesToPrepareMovementsAdapter(action.payload.response)
            );
        case actionTypes.BROKER_CONTRACT_VALUES_SUCCESS:
            return getValuesSuccess(
                state,
                action.payload.contractId,
                action.payload.data.get('stocks')
            );
        case actionTypes.BROKER_CONTRACT_VALUES_FAILURE:
            return state.mergeDeep({
                [action.payload.contractId]: {
                    isFetching: false,
                    error: 'Contract not found' //@hardcoded
                }
            });
        case actionTypes.BROKER_CRYPTO_CONTRACT_VALUES_SUCCESS:
            const results = action.payload.data?.results || [];
 
            return getCryptoSuccess(
                state,
                action.payload.portfolioId,
                Immutable.fromJS(results),
                action.payload.contract,
                action.payload.catalogueResponse
            );
        case actionTypes.BROKER_CRYPTO_CONTRACT_VALUES_FAILURE:
            return state.mergeDeep({
                [action.payload.portfolioId]: {
                    isFetching: false,
                    error: 'Portfolio not found'
                }
            });
        case actionTypes.BROKER_CRYPTO_CONTRACT_FILTER_REQUEST:
            return state.mergeDeep({
                [action.payload.portfolioId]: {
                    isFetching: true,
                }
            });
        case actionTypes.BROKER_CRYPTO_CONTRACT_FILTER_SUCCESS:
            const newFilteredIds = Immutable.fromJS(
                action?.payload?.data?.results?.map(movement => movement.id) || []
            );
            return state.setIn(
                [action.payload.portfolioId, 'cryptoFilteredIds'],
                newFilteredIds
            ).setIn(
                [action.payload.portfolioId, 'isFetching'],
                false
            ).setIn(
                [action.payload.portfolioId, 'error'],
                ''
            );
        case actionTypes.BROKER_CRYPTO_CONTRACT_FILTER_FAILURE:
            return state.mergeDeep({
                [action.payload.portfolioId]: {
                    isFetching: false,
                    error: 'Contract not found', //@hardcoded
                }
            });
        case actionTypes.BROKER_CRYPTO_CONTRACT_CLEAR_FILTER:
            const walletNumber = action.payload.walletNumber;
            const currentById = state.getIn([walletNumber, 'byId']);
            if (!currentById) {
                return state;
            }
            const allMovementIds = [];
            currentById.forEach((valueData) => {
                const movements = valueData.get('movements');
                if (movements) {
                    movements.forEach((movement) => {
                        const movementId = movement.get('id');
                        if (movementId && !allMovementIds.includes(movementId)) {
                            allMovementIds.push(movementId);
                        }
                    });
                }
            });

            return state.setIn(
                [walletNumber, 'cryptoFilteredIds'],
                Immutable.fromJS(allMovementIds)
            );
        default:
            return state;
    }
}

module.exports = brokerContractValues;
